import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Star, MapPin, CheckCircle, ChevronDown, ChevronUp, Play, Pause, Volume2, VolumeX, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';

// Extended testimonials data
// VIDEO PLAYBACK TEMPORARILY DISABLED - Videos contain incorrect content
// Will be re-enabled when correct testimonial videos are available
const VIDEOS_DISABLED = true; // Toggle this to re-enable videos

const allTestimonials = [
  {
    id: 1,
    name: 'Rajesh Kumar',
    role: 'Software Engineer',
    previousCompany: 'TCS, Bangalore',
    destination: 'Canada',
    currentCompany: 'Tech Corp, Toronto',
    thumbnail: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
    videoUrl: VIDEOS_DISABLED ? null : 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    quote: 'Zafco made my Canada PR journey incredibly smooth. Within 8 months, I got my PR and landed a great job in Toronto! The team was supportive throughout the entire process.',
    fullStory: 'When I first approached Zafco, I had a CRS score of 445 which was below the cutoff. The team helped me identify ways to improve my score through IELTS retake and securing a provincial nomination from Ontario. Within 6 months, my score jumped to 520 and I received my ITA. The documentation process was seamless with their guidance.',
    rating: 5,
    flag: '🇨🇦',
    year: '2025',
    program: 'Express Entry - Ontario PNP',
    processingTime: '8 months'
  },
  {
    id: 2,
    name: 'Priya Sharma',
    role: 'Registered Nurse',
    previousCompany: 'Apollo Hospital, Delhi',
    destination: 'Australia',
    currentCompany: 'Royal Melbourne Hospital',
    thumbnail: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=300&fit=crop',
    videoUrl: VIDEOS_DISABLED ? null : 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    quote: 'As a nurse, I was worried about the complex AHPRA process. Zafco guided me every step of the way. Now I\'m happily working in Melbourne with a great salary and benefits!',
    fullStory: 'The nursing registration process in Australia seemed overwhelming with AHPRA requirements, IELTS, and skills assessment. Zafco\'s healthcare immigration specialists walked me through each step. They even connected me with other nurses who had successfully made the move.',
    rating: 5,
    flag: '🇦🇺',
    year: '2025',
    program: 'Subclass 189 - Skilled Independent',
    processingTime: '12 months'
  },
  {
    id: 3,
    name: 'Amit Patel',
    role: 'Civil Engineer',
    previousCompany: 'L&T, Mumbai',
    destination: 'Germany',
    currentCompany: 'Siemens, Munich',
    thumbnail: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=300&fit=crop',
    videoUrl: VIDEOS_DISABLED ? null : 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
    quote: 'The Germany Opportunity Card was new and confusing. Zafco\'s expertise helped me understand the points system and secure my visa quickly. Best decision I ever made!',
    fullStory: 'I was skeptical about the new Opportunity Card program, but Zafco had already researched it thoroughly. They helped with my qualification recognition, German language preparation resources, and even mock interviews. I landed in Munich with a job offer from Siemens within 4 months.',
    rating: 5,
    flag: '🇩🇪',
    year: '2025',
    program: 'Germany Opportunity Card',
    processingTime: '5 months'
  },
  {
    id: 4,
    name: 'Mohammed Ali',
    role: 'Electrical Technician',
    previousCompany: 'Larsen & Toubro, Chennai',
    destination: 'Saudi Arabia',
    currentCompany: 'ARAMCO, Dammam',
    thumbnail: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=300&fit=crop',
    videoUrl: VIDEOS_DISABLED ? null : 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
    quote: 'Zafco\'s Saudi visa processing unit is excellent. Being empanelled with the Saudi Embassy, they made my documentation and visa stamping incredibly fast!',
    fullStory: 'I needed to join ARAMCO urgently as they had a project deadline. Zafco\'s dedicated Saudi visa unit processed my entire documentation in record time. Being empanelled with the Royal Embassy of Saudi Arabia, New Delhi, they submitted my visa application directly. Got my employment visa stamped in just 3 weeks!',
    rating: 5,
    flag: '🇸🇦',
    year: '2025',
    program: 'Saudi Employment Visa',
    processingTime: '3 weeks'
  },
  {
    id: 5,
    name: 'Sneha Reddy',
    role: 'IT Consultant',
    previousCompany: 'Infosys, Hyderabad',
    destination: 'UK',
    currentCompany: 'Deloitte, London',
    thumbnail: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=300&fit=crop',
    videoUrl: VIDEOS_DISABLED ? null : 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4',
    quote: 'Getting a UK Skilled Worker visa seemed impossible until I met the Zafco team. Their connections with UK employers were invaluable! Now I\'m living my dream in London.',
    fullStory: 'Finding a UK employer willing to sponsor was my biggest challenge. Zafco\'s network of partner companies and recruitment agencies helped me secure interviews with top firms. The salary negotiation support ensured I met the threshold requirements.',
    rating: 5,
    flag: '🇬🇧',
    year: '2024',
    program: 'UK Skilled Worker Visa',
    processingTime: '6 months'
  },
  {
    id: 6,
    name: 'Farhan Sheikh',
    role: 'Construction Supervisor',
    previousCompany: 'Shapoorji Pallonji, Pune',
    destination: 'UAE',
    currentCompany: 'EMAAR, Dubai',
    thumbnail: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=300&fit=crop',
    videoUrl: VIDEOS_DISABLED ? null : 'https://storage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4',
    quote: 'Zafco helped me secure a great position with EMAAR in Dubai. Their Gulf recruitment team understands the market perfectly. The process was seamless!',
    fullStory: 'The Gulf job market can be tricky to navigate on your own. Zafco\'s recruitment team had direct connections with EMAAR and arranged my interview within a week. They handled everything from offer letter verification to visa processing.',
    rating: 5,
    flag: '🇦🇪',
    year: '2025',
    program: 'UAE Employment Visa',
    processingTime: '4 weeks'
  },
  {
    id: 7,
    name: 'Vikram Singh',
    role: 'Mechanical Engineer',
    previousCompany: 'Mahindra, Pune',
    destination: 'Australia',
    currentCompany: 'BHP, Perth',
    thumbnail: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=300&fit=crop',
    quote: 'The mining sector in Western Australia was exactly what I was looking for. Zafco connected me with the right opportunities and handled my visa flawlessly.',
    fullStory: 'Engineers Australia assessment was crucial for my application. Zafco\'s engineers helped me prepare my CDR and career episodes perfectly. The WA state nomination came through quickly given the mining demand.',
    rating: 5,
    flag: '🇦🇺',
    year: '2024',
    program: 'Subclass 190 - WA State Nomination',
    processingTime: '14 months'
  },
  {
    id: 8,
    name: 'Meera Krishnan',
    role: 'Accountant',
    previousCompany: 'KPMG, Kolkata',
    destination: 'Canada',
    currentCompany: 'PwC, Vancouver',
    thumbnail: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=300&fit=crop',
    quote: 'British Columbia was my dream destination. Zafco\'s BC PNP expertise helped me navigate the Tech Pilot program successfully. Vancouver life is amazing!',
    fullStory: 'Being an accountant, I wasn\'t sure if I qualified for the BC Tech Pilot. Zafco identified that my fintech experience made me eligible. The entire process from ECA to PR landing took just 11 months.',
    rating: 5,
    flag: '🇨🇦',
    year: '2025',
    program: 'BC PNP Tech Pilot',
    processingTime: '11 months'
  },
  {
    id: 9,
    name: 'Abdul Rahman',
    role: 'Plumber',
    previousCompany: 'Self-employed, Kerala',
    destination: 'Qatar',
    currentCompany: 'Qatar Gas',
    thumbnail: 'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=400&h=300&fit=crop',
    quote: 'As a skilled tradesman, I wasn\'t sure about overseas opportunities. Zafco opened my eyes to the Gulf market demand. Now earning 5x what I made in India!',
    fullStory: 'Zafco\'s bulk recruitment team was handling positions for Qatar Gas. They helped me with skills testing, medical examination, and all documentation. The entire process was transparent with no hidden charges.',
    rating: 5,
    flag: '🇶🇦',
    year: '2025',
    program: 'Qatar Employment Visa',
    processingTime: '6 weeks'
  },
  {
    id: 10,
    name: 'Pooja Agarwal',
    role: 'Pharmacist',
    previousCompany: 'Cipla, Mumbai',
    destination: 'UK',
    currentCompany: 'NHS, Birmingham',
    thumbnail: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=400&h=300&fit=crop',
    quote: 'GPhC registration seemed daunting, but Zafco\'s healthcare team had specialists who understood the UK pharmacy requirements. Now working with the NHS!',
    fullStory: 'The GPhC OSPAP program was essential for my UK registration. Zafco connected me with UK pharmacy courses and helped prepare my documentation. The NHS job came through their employer network.',
    rating: 5,
    flag: '🇬🇧',
    year: '2025',
    program: 'UK Health & Care Worker Visa',
    processingTime: '9 months'
  },
  {
    id: 11,
    name: 'Suresh Menon',
    role: 'Welder',
    previousCompany: 'Cochin Shipyard, Kochi',
    destination: 'Saudi Arabia',
    currentCompany: 'SABIC, Jubail',
    thumbnail: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=400&h=300&fit=crop',
    quote: 'Zafco\'s trade skills testing facility in Delhi was professional. They prepared me for the Saudi employer\'s welding test, and I cleared it on the first attempt!',
    fullStory: 'Getting a welding job in Saudi Arabia requires passing practical tests. Zafco arranged my training and testing at their facility. With their Saudi Embassy empanelment, my visa was processed in record time.',
    rating: 5,
    flag: '🇸🇦',
    year: '2025',
    program: 'Saudi Trade Worker Visa',
    processingTime: '4 weeks'
  },
  {
    id: 12,
    name: 'Neha Joshi',
    role: 'Marketing Manager',
    previousCompany: 'HUL, Delhi',
    destination: 'Germany',
    currentCompany: 'BMW Group, Munich',
    thumbnail: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop',
    quote: 'The EU Blue Card process was new to me. Zafco\'s Europe team had deep knowledge of German immigration. Working at BMW now is surreal!',
    fullStory: 'Germany\'s qualification recognition process seemed lengthy but Zafco helped expedite it. They also provided German language resources that helped me integrate faster. The salary negotiation support was crucial for Blue Card eligibility.',
    rating: 5,
    flag: '🇩🇪',
    year: '2025',
    program: 'EU Blue Card',
    processingTime: '6 months'
  }
];

// Video Testimonial Card Component
const VideoTestimonialCard = ({ testimonial }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [expandedStory, setExpandedStory] = useState(false);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const hasVideo = !!testimonial.videoUrl;

  return (
    <motion.div
      className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`testimonial-${testimonial.id}`}
    >
      {/* Video/Image Section */}
      {hasVideo ? (
        <div 
          className="relative aspect-video bg-gray-900 cursor-pointer group"
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(false)}
          onClick={togglePlay}
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            poster={testimonial.thumbnail}
            muted={isMuted}
            loop
            playsInline
            preload="metadata"
          >
            <source src={testimonial.videoUrl} type="video/mp4" />
          </video>

          {/* Play/Pause Overlay */}
          <div className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 ${isPlaying && !showControls ? 'opacity-0' : 'opacity-100'}`}>
            <motion.button
              className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
              }}
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 text-zafco-blue" />
              ) : (
                <Play className="w-6 h-6 text-zafco-blue ml-1" />
              )}
            </motion.button>
          </div>

          {/* Video Badge */}
          <div className="absolute top-3 left-3 flex items-center gap-1 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
            <Video className="w-3 h-3" />
            <span>Video</span>
          </div>

          {/* Mute Button */}
          <button
            className={`absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-all ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleMute();
            }}
          >
            {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>

          {/* Country Badge */}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <span className="text-2xl">{testimonial.flag}</span>
            <span className="bg-white/90 px-2 py-1 rounded text-xs font-semibold text-zafco-blue">{testimonial.year}</span>
          </div>
        </div>
      ) : (
        <div className="relative h-28 bg-gradient-to-r from-zafco-blue to-zafco-teal">
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <span className="text-2xl">{testimonial.flag}</span>
            <span className="bg-white/90 px-2 py-1 rounded text-xs font-semibold text-zafco-blue">{testimonial.year}</span>
          </div>
          <div className="absolute -bottom-8 left-6">
            <div className="w-16 h-16 rounded-full border-4 border-white shadow-lg overflow-hidden">
              <img 
                src={testimonial.thumbnail} 
                alt={`${testimonial.name} — ZAFCO success story`}
                className="w-full h-full object-cover"
                width="64"
                height="64"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`${hasVideo ? 'pt-4' : 'pt-12'} pb-6 px-6`}>
        {/* Person Info */}
        <div className="flex items-start gap-3 mb-3">
          {hasVideo && (
            <img 
              src={testimonial.thumbnail} 
              alt={`${testimonial.name} headshot — ZAFCO client testimonial`}
              className="w-10 h-10 rounded-full object-cover border-2 border-zafco-teal"
              width="40"
              height="40"
              loading="lazy"
              decoding="async"
            />
          )}
          <div>
            <h4 className="font-bold text-lg text-zafco-blue">{testimonial.name}</h4>
            <p className="text-sm text-gray-500">{testimonial.role}</p>
          </div>
        </div>

        {/* Journey */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3 bg-gray-50 p-2 rounded-lg">
          <MapPin className="w-3 h-3 text-zafco-teal flex-shrink-0" />
          <span className="truncate">{testimonial.previousCompany}</span>
          <span>→</span>
          <span className="font-medium text-zafco-blue truncate">{testimonial.currentCompany}</span>
        </div>

        {/* Rating */}
        <div className="flex gap-1 mb-3">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star key={`${testimonial.id}-star-${i}`} className="w-4 h-4 text-zafco-gold fill-zafco-gold" />
          ))}
        </div>

        {/* Quote */}
        <div className="relative mb-4">
          <Quote className="absolute -top-2 -left-2 w-6 h-6 text-zafco-teal/20" />
          <p className="text-gray-600 text-sm leading-relaxed pl-4 italic">
            "{testimonial.quote}"
          </p>
        </div>

        {/* Expand Story Button */}
        {testimonial.fullStory && (
          <button
            onClick={() => setExpandedStory(!expandedStory)}
            className="text-zafco-teal text-sm font-medium mb-3 hover:underline flex items-center gap-1"
          >
            {expandedStory ? (
              <>Show less <ChevronUp className="w-4 h-4" /></>
            ) : (
              <>Read full story <ChevronDown className="w-4 h-4" /></>
            )}
          </button>
        )}

        {/* Expanded Story */}
        <AnimatePresence>
          {expandedStory && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <p className="text-gray-500 text-sm mb-4 bg-gray-50 p-3 rounded-lg">
                {testimonial.fullStory}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Program Tags */}
        <div className="flex flex-wrap gap-2 pt-3 border-t">
          <span className="bg-zafco-blue/10 text-zafco-blue text-xs px-2 py-1 rounded-full font-medium">
            {testimonial.program}
          </span>
          <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            {testimonial.processingTime}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const TestimonialsPage = () => {
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('all'); // 'all', 'video'

  const countries = ['all', 'Canada', 'Australia', 'Germany', 'UK', 'Saudi Arabia', 'UAE', 'Qatar'];

  const filteredTestimonials = allTestimonials
    .filter(t => filter === 'all' || t.destination === filter)
    .filter(t => viewMode === 'all' || (viewMode === 'video' && t.videoUrl));

  return (
    <div data-testid="testimonials-page">
      <PageHeader 
        title="Success Stories" 
        subtitle="Real stories from professionals who achieved their overseas career dreams with Zafco's guidance"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Success Stories', path: '/testimonials' }
        ]}
      />

      {/* Government Approval Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-3">
        <div className="container mx-auto px-6 text-center">
          <p className="text-sm md:text-base">
            <span className="font-bold">Government of India Approved</span> – MEA RC No: B-0762/DEL/PER/1000+/5/9148/2015
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <section className="py-8 bg-gray-50 sticky top-0 z-20">
        <div className="container mx-auto px-6">
          {/* View Mode Toggle - Video filter hidden while videos are disabled */}
          <div className="flex justify-center gap-2 mb-4">
            <Button
              variant={viewMode === 'all' ? 'default' : 'outline'}
              className={viewMode === 'all' ? 'bg-zafco-blue' : ''}
              onClick={() => setViewMode('all')}
              size="sm"
              data-testid="view-all-btn"
            >
              All Stories
            </Button>
            {/* Video Testimonials button - temporarily hidden while videos are disabled */}
            {!VIDEOS_DISABLED && (
              <Button
                variant={viewMode === 'video' ? 'default' : 'outline'}
                className={viewMode === 'video' ? 'bg-red-500' : ''}
                onClick={() => setViewMode('video')}
                size="sm"
                data-testid="view-videos-btn"
              >
                <Video className="w-4 h-4 mr-1" />
                Video Testimonials
              </Button>
            )}
          </div>

          {/* Country Filter */}
          <div className="flex flex-wrap gap-3 justify-center">
            {countries.map((country) => (
              <Button
                key={country}
                variant={filter === country ? 'default' : 'outline'}
                className={filter === country ? 'bg-zafco-blue' : ''}
                onClick={() => setFilter(country)}
                size="sm"
                data-testid={`filter-${country.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {country === 'all' ? 'All Countries' : country}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          {filteredTestimonials.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No testimonials found for the selected filters.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTestimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <VideoTestimonialCard testimonial={testimonial} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-zafco-blue to-zafco-teal">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-3xl md:text-4xl font-bold font-montserrat mb-4">
                Ready to Write Your Success Story?
              </h2>
              <p className="text-white/80 mb-6">
                Join thousands of successful professionals who trusted Zafco for their overseas career journey.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="text-4xl font-bold">10,000+</div>
                  <div className="text-white/70 text-sm">Successful Placements</div>
                </div>
                <div>
                  <div className="text-4xl font-bold">98%</div>
                  <div className="text-white/70 text-sm">Success Rate</div>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-sm text-white/90">
                  <strong>Government of India Approved</strong><br />
                  MEA RC No: B-0762/DEL/PER/1000+/5/9148/2015
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6">
              <h3 className="text-xl font-bold text-zafco-blue mb-4">Get Your Free Assessment</h3>
              <ConsultationForm compact />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
